import React, { useCallback, useState, useEffect } from "react";
import { Form, Image, Message, Button } from "semantic-ui-react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import { User, Role, Company, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { ENV } from "../../../../utils";
import { isAdmin, isMaster, isEditor, isReviewer } from "../../../../utils/checkPermission";
import { initialValues, validationSchema } from "./UserForm.form";
import "./UserForm.scss";
import { useLanguage } from "../../../../contexts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SitesMultiSelect } from "../../../Shared";
import { PeerAccessFields, initialPeerAccess, saveUserPeerAccess } from "./PeerAccessFields";

const userController = new User();
const roleController = new Role();
const companyController = new Company();
const siteController = new Site();

export function UserForm(props) {
  const { close, onReload, user, defaultCompanyId } = props;
  const {
    accessToken,
    user: { role, company, sites: creatorSites },
  } = useAuth();
  const [listRoles, setListRoles] = useState([]);
  const [listCompanies, setListCompanies] = useState([]);
  const [listSites, setListSites] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [siteData, setSiteData] = useState([]);
  const [error, setError] = useState("");
  // Acceso a la otra app Sustemia (lo deciden admin o master)
  const [peerAccessInitial] = useState(() => initialPeerAccess(user));
  const [peerAccess, setPeerAccess] = useState(peerAccessInitial);

  const { translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  useEffect(() => {
    roleController.getRoles(accessToken, true).then((response) => {
      setListRoles(response);
    });
  }, []);

  useEffect(() => {
    if (isMaster(role)) {
      companyController.getCompanies(accessToken, true).then((response) => {
        setListCompanies(response);
      });
    } else if (isAdmin(role) && company) {

      siteController
        .getSitesByCompany(accessToken, company, true)
        .then((response) => {
          setListSites(response);
        });
        companyController
        .getCompany(accessToken, company)
        .then((response) => {
          setCompanyData(response);
        });
      if (creatorSites && creatorSites.length > 0) {
        siteController.getSite(accessToken, creatorSites[0]).then((response) => {
          setSiteData(response);
        });
      }
    }
  }, []);

  const formik = useFormik({
    initialValues: initialValues(user),
    validationSchema: validationSchema(user, role),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (peerAccess.enabled && !peerAccess.roleName) {
        setError(t("peer_role_required"));
        return;
      }
      try {
        let peerWarning = null;
        if (!user) {
          if (isAdmin(role)) {
            formValue.company = companyData._id;
            formValue.sites = [siteData._id];
          }
          // Llamamos a la función para eliminar los campos con valores vacíos
          const cleanedData = removeNullFields(formValue);
          const response = await userController.createUser(
            accessToken,
            cleanedData
          );
          if (response.code && response.code === 500 && response.code === 400) {
               toast.error("Error al crear el usuario!");
          }else {
               toast.success("Usuario creado correctamente!");
               peerWarning = await saveUserPeerAccess(accessToken, {
                 email: cleanedData.email, initial: peerAccessInitial, value: peerAccess,
               });
          }
        } else {
          if (isAdmin(role) && !user.company) {
            formValue.company = companyData._id;
          } else if (isAdmin(role) && !user.sites?.length) {
            formValue.sites = [siteData._id];
          }
          const newData = removeNullFields(formValue);

          // Editor/Reviewer editando su propio perfil → ruta segura sin ID en URL
          const isOwnProfile = !isMaster(role) && !isAdmin(role);
          let response1;
          if (isOwnProfile) {
            response1 = await userController.updateOwnProfile(accessToken, newData);
          } else {
            response1 = await userController.updateUser(accessToken, user._id, newData);
          }
          if(response1.code && response1.code ===200){
            toast.success("Usuario actualizado correctamente!");
            if (!isOwnProfile) {
              peerWarning = await saveUserPeerAccess(accessToken, {
                user, initial: peerAccessInitial, value: peerAccess,
              });
            }
          }

        }
        if (peerWarning) window.alert(peerWarning);
         // Espera 120 ms antes de cerrar el modal o hacer un reload
         setTimeout(() => {
          onReload(true);
          close();
        }, 1200);
      } catch (error) {
        setError(error.msg);
        console.error(error);
      }
    },
  });

  useEffect(() => {
    if (isMaster(role) && defaultCompanyId && !user) {
      formik.setFieldValue("company", defaultCompanyId);
    }
  }, [defaultCompanyId]);

  useEffect(() => {
    if (isMaster(role) && formik.values.company) {
      siteController
        .getSitesByCompany(accessToken, formik.values.company, true)
        .then((response) => {
          setListSites(response);
        });
    }
  }, [formik.values.company]);

  useEffect(() => {
    if (isMaster(role)) {
      const selectedRole = listRoles.find((r) => r._id === formik.values.role);
      if (isAdmin(selectedRole)) {
        formik.setFieldValue("sites", []);
      }
    }
  }, [formik.values.role]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      formik.setFieldValue("avatar", URL.createObjectURL(file));
      formik.setFieldValue("fileAvatar", file);
    },
    [formik]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg"], "image/png": [".png"] },
    onDrop,
  });

  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return `${ENV.BASE_PATH}/${formik.values.avatar}`;
    }
    return image.noAvatar;
  };

  return (
    <Form className="user-form" onSubmit={formik.handleSubmit}>
      {error.length > 0 ? (
        <Message visible={error.length > 0} negative>
          {error}
        </Message>
      ) : null}

      <Form.Group widths="equal">
        <Form.Input
          label={<label><span style={{ color: "red", marginRight: 4 }}>*</span>{t("name")}</label>}
          name="firstname"
          onChange={formik.handleChange}
          value={formik.values.firstname}
          error={formik.errors.firstname}
        />
        <Form.Input
          label={<label><span style={{ color: "red", marginRight: 4 }}>*</span>{t("lastname")}</label>}
          name="lastname"
          onChange={formik.handleChange}
          value={formik.values.lastname}
          error={formik.errors.lastname}
        />
      </Form.Group>

      <Form.Group widths="equal" className="half-width-group">
        {isMaster(role) ? (
          <Form.Dropdown
            label={t("company")}
            placeholder={t("select")}
            options={listCompanies.map((ds) => ({
              key: ds._id,
              text: ds.name,
              value: ds._id,
            }))}
            selection
            onChange={(_, data) => formik.setFieldValue("company", data.value)}
            value={formik.values.company}
            error={formik.errors.company}
          />
        ) : isAdmin(role) && company ? (
          siteData.length === 0 ? (
            <Form.Input
              label={t("company")}
              name="company"
              onChange={formik.handleChange}
              value={companyData ? companyData.name : ""}
              error={formik.errors.company}
            />
          ) : (
            <Form.Input
              label={t("site")}
              name="site"
              onChange={formik.handleChange}
              value={siteData ? siteData.name : ""}
              error={formik.errors.site}
            />
          )
        ) : (
          <Form.Input
            label={t("site")}
            name="site"
            onChange={formik.handleChange}
            value={siteData ? siteData.name : ""}
            error={formik.errors.site}
          />
        )}
      </Form.Group>

      {/* Sitio (multi-selección): disparador compacto de una línea + popup
          con checkboxes, en vez del dropdown con chips (se aplastaban o
          crecían a varias filas con varios sitios elegidos). */}
      {isMaster(role) &&
        !isAdmin(listRoles.find((r) => r._id === formik.values.role)) && (
          <Form.Field error={!!formik.errors.sites}>
            <label>{t("site")}</label>
            <SitesMultiSelect
              sites={listSites}
              value={formik.values.sites}
              onChange={(v) => formik.setFieldValue("sites", v)}
              error={formik.errors.sites}
              t={t}
            />
          </Form.Field>
        )}
      {isAdmin(role) && company && siteData.length === 0 && (
        <Form.Field error={!!formik.errors.sites}>
          <label>{t("site")}</label>
          <SitesMultiSelect
            sites={listSites}
            value={formik.values.sites}
            onChange={(v) => formik.setFieldValue("sites", v)}
            error={formik.errors.sites}
            t={t}
          />
        </Form.Field>
      )}

      <Form.Group widths="equal">
        <Form.Input
          label={t("sector")}
          name="sector"
          onChange={formik.handleChange}
          value={formik.values.sector}
          error={formik.errors.sector}
        />
        <Form.Input
          label={t("position")}
          name="position"
          onChange={formik.handleChange}
          value={formik.values.position}
          error={formik.errors.position}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          label={<label><span style={{ color: "red", marginRight: 4 }}>*</span>{t("email")}</label>}
          name="email"
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
        />
        {isMaster(role) || isAdmin(role) ? (
          <Form.Dropdown
            label={<label><span style={{ color: "red", marginRight: 4 }}>*</span>{t("role")}</label>}
            placeholder={t("select")}
            options={listRoles.map((ds) => ({
              key: ds._id,
              text: ds.name,
              value: ds._id,
            }))}
            selection
            onChange={(_, data) => formik.setFieldValue("role", data.value)}
            value={formik.values.role}
            error={formik.errors.role}
          />
        ) : null}
      </Form.Group>

      {(isMaster(role) || isAdmin(role)) &&
        !isMaster(listRoles.find((r) => r._id === formik.values.role)) && (
          <PeerAccessFields
            accessToken={accessToken}
            companyId={formik.values.company || user?.company?._id || user?.company || company}
            value={peerAccess}
            onChange={setPeerAccess}
            t={t}
          />
        )}

      <Form.Input
        label={<label><span style={{ color: "red", marginRight: 4 }}>*</span>{t("password")}{user && <span style={{ color: "#999", fontWeight: 400, marginLeft: 6, fontSize: "0.85em" }}>({t("leave_blank_to_keep")})</span>}</label>}
        type="password"
        name="password"
        onChange={formik.handleChange}
        value={formik.values.password}
        error={formik.errors.password}
      />

      <div className="user-form__actions">
        <Button
          type="submit"
          primary
          className="user-form__btn-save"
          loading={formik.isSubmitting}
        >
          {!user ? t("save") : t("update")}
        </Button>
        <Button
          type="button"
          secondary
          className="user-form__btn-cancel"
          onClick={close}
        >
          {t("cancel")}
        </Button>
      </div>

      
               {/* Este es el contenedor que maneja las tostadas */}
                  <ToastContainer
                    position="top-center"
                    autoClose={1200}
                    hideProgressBar
                    newestOnTop={false}
                    closeOnClick={false}
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                  />

    </Form>
  );
}

function removeNullFields(obj) {
  // Recorre las propiedades del objeto
  for (const key in obj) {
    // Verifica si el campo es null
    if (obj[key] === null) {
      // Elimina el campo del objeto
      delete obj[key];
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      // Si el valor es un objeto, llama a la función recursivamente
      removeNullFields(obj[key]);
    }
  }
  return obj;
}
